import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** Visual severity for toast styling and default dismiss timing. */
export type ToastSeverity = 'error' | 'warning' | 'success' | 'info';

export interface ToastOptions {
  /** Defaults to `info`. */
  severity?: ToastSeverity;
  /**
   * Auto-dismiss delay in milliseconds.
   * Use `0` to keep the toast until the user closes it.
   * Defaults by severity: error 4500, warning 4000, others 3500.
   */
  duration?: number;
}

export interface ToastItem {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration: number;
  /** True while the exit animation runs. */
  leaving?: boolean;
}

const DEFAULT_DURATION: Record<ToastSeverity, number> = {
  error: 4500,
  warning: 4000,
  success: 3500,
  info: 3500,
};

const EXIT_MS = 220;
const MAX_VISIBLE = 5;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = new BehaviorSubject<ToastItem[]>([]);
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private seq = 0;

  readonly toasts$ = this._toasts.asObservable();

  /**
   * Show a toast. Existing callers using `show(message)` keep working
   * (defaults to `info`).
   */
  show(message: string, options?: ToastOptions | ToastSeverity): void {
    const text = String(message ?? '').trim();
    if (!text) return;

    const opts: ToastOptions =
      typeof options === 'string' ? { severity: options } : options ?? {};
    const severity: ToastSeverity = opts.severity ?? 'info';
    const duration =
      opts.duration !== undefined ? opts.duration : DEFAULT_DURATION[severity];

    if (severity === 'error' && this._toasts.value.some(toast => !toast.leaving && toast.message === text)) {
      return;
    }

    const id = `toast-${++this.seq}-${Date.now()}`;
    const item: ToastItem = { id, message: text, severity, duration };

    const next = [item, ...this._toasts.value].slice(0, MAX_VISIBLE);
    this._toasts.next(next);

    if (duration > 0) {
      const timer = setTimeout(() => this.dismiss(id), duration);
      this.timers.set(id, timer);
    }
  }

  success(message: string, duration?: number): void {
    this.show(message, { severity: 'success', duration });
  }

  error(message: string, duration?: number): void {
    this.show(message, { severity: 'error', duration });
  }

  warning(message: string, duration?: number): void {
    this.show(message, { severity: 'warning', duration });
  }

  info(message: string, duration?: number): void {
    this.show(message, { severity: 'info', duration });
  }

  dismiss(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }

    const current = this._toasts.value;
    const target = current.find(t => t.id === id);
    if (!target || target.leaving) return;

    this._toasts.next(
      current.map(t => (t.id === id ? { ...t, leaving: true } : t)),
    );

    setTimeout(() => {
      this._toasts.next(this._toasts.value.filter(t => t.id !== id));
    }, EXIT_MS);
  }

  dismissAll(): void {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    this._toasts.next([]);
  }
}
